import * as React from "react";
import { connect } from "react-redux";
import areEqual from "fbjs/lib/areEqual";

import { scrollToUnitAction, unitHighlightAction } from "../../../Engine/ActionCreators/NavigationActionCreators";
import {
    addMultipleUnitInstanceAction,
    addPageAction,
    clearMultipleUnitInstanceAction,
    clearPageAction,
    ClearPageMethod,
    copyMultipleUnitInstanceAction,
    removeMultipleUnitInstanceAction,
    removePageAction,
} from "../../../Engine/ActionCreators/UnitActionCreators";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { currentViewModelSelect, ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import { PathResolver, withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withVisibilityProps } from "../../withVisibilityProps";
import { isUnitIdEquals, SectionName, UnitId, unitIdToLegacyUnitId } from "../Unit/UnitId/UnitId";
import { UnitIdResolver, withResolveUnitId } from "../../withResolveUnitId";
import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { UnitIdImmutableSet, UnitIdSet } from "../Unit/UnitId/UnitIdImmutableSet";
import { isChildOfType } from "../../TypingUtils";
import { isUnitFilled as isOptionalAndEmpty } from "../Unit/Internals/OptionalPages/ResolveOptionalPage";
import { schedulerWrapper, TaskPriority } from "../../../Helpers/Scheduler/SchedulerApi";
import { IterUtils } from "../../../../../Common/IterableUtils";
import { StringArrayKey } from "../../../../../Common/StringArrayKey";
import { removeAttachmentAction } from "../../../Engine/ActionCreators/AttachmentActionCreators";
import { PropsPageActions } from "../Unit/Types";

import { UnitItem } from "./UnitItem";
import { getChildUnitIds, getRemoveLabelForChangeSet } from "./UnitItemHelpers";

interface UnitItemOwnProps {
    id?: UnitId;
    multiple: boolean;
    optional?: boolean;
    unitPath?: AbsoluteModelPath;
    children?: React.ReactNode;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    visible?: EvaluableProp<boolean>;
    viewModelSelect: ViewModelSelect;
    resolvePath: PathResolver;
    resolveUnitId: UnitIdResolver;
    name?: string;
    hasAttachment?: boolean;
    pageActions?: PropsPageActions;
}
const mapStateToProps = (state: FullFormState, ownProps: UnitItemOwnProps) => {
    const { multiple, viewModelSelect, unitPath } = ownProps;
    const resolvedPath = unitPath ? ownProps.resolvePath(unitPath) : unitPath;
    const resolvedUnitId = ownProps.resolveUnitId(ownProps.id);
    const { emptyUnits } = state;

    const hidden = resolvedPath != undefined && viewModelSelect.pathViewModel(state, resolvedPath).visible === false;
    const emptyUnitsVisible = getEmptyUnitsVisible(state);

    const hasVisibleChildren = React.Children.toArray(ownProps.children).some(
        child =>
            isChildOfType(child, UnitItemContainer) &&
            (ownProps.processEvaluableProp(state, child.props.visible) ?? false)
    );

    const selfFilled = resolvedUnitId == undefined || !UnitIdSet.hasItem(emptyUnits, resolvedUnitId);
    const filled = !multiple && hasVisibleChildren ? isUnitFilled(ownProps, selfFilled, emptyUnits) : selfFilled;
    const visible = ownProps.processEvaluableProp(state, ownProps.visible);
    const childUnitIds = getChildUnitIds(ownProps);

    const currentUnit = state.highlightUnit;
    const unitId = ownProps.id;

    let currentInstance: string | undefined;
    if (currentUnit != undefined) {
        if (
            ownProps.id?.pageId === currentUnit.pageId &&
            areEqual(unitId?.parentInstances, currentUnit.parentInstances)
        ) {
            currentInstance = currentUnit.instance;
        } else if (childUnitIds.some(child => child.pageId === currentUnit.pageId)) {
            currentInstance = IterUtils.last(currentUnit.parentInstances ?? []);
        } else {
            const key = new StringArrayKey([unitId?.pageId ?? "", ...(unitId?.parentInstances ?? [])]);
            const prevHighlightedInstance = state.highlightUnitsInfo?.get(key);
            currentInstance = prevHighlightedInstance;
        }
    }

    let pageActions = ownProps.pageActions;

    if (ownProps.hasAttachment) {
        pageActions = {
            ...ownProps.pageActions,
            actions: {
                ...ownProps.pageActions?.actions,
                copy: {
                    hideAction: true,
                },
                clear: {
                    hideAction: true,
                },
            },
        };
    }

    const isReadOnly = state.settings.isReadOnly;

    return {
        id: resolvedUnitId,
        unitPath: resolvedPath,
        hidden: Boolean(hidden || !(emptyUnitsVisible || filled) || (isReadOnly && ownProps.hasAttachment)),
        emptyUnitsVisible: emptyUnitsVisible,
        filled: filled,
        visible: isReadOnly && !filled ? false : visible,
        isCurrent: isUnitIdEquals(resolvedUnitId, state.highlightUnit),
        hasVisibleChildren: hasVisibleChildren,
        isChildItemActive: childUnitIds.some(childUnit => childUnit.pageId === state.highlightUnit?.pageId),
        childUnitIds: childUnitIds,
        isReadOnly: isReadOnly,
        currentInstance: currentInstance,
        name: ownProps.name,
        isOnlyPlus: ["3000101", "3000102", "3000103"].includes(state.settings.gfv),
        pageActions: pageActions,
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: UnitItemOwnProps) => ({
    onAddMultipleUnitInstance: (bindingPath: AbsoluteResolvedModelPath, unitId: UnitId, newIndex?: number) => {
        schedulerWrapper(
            () => {
                dispatch(addMultipleUnitInstanceAction(bindingPath, unitId, newIndex));
            },
            { priority: TaskPriority.UserBlocking }
        );
    },
    onAddSingleUnit: () => {
        schedulerWrapper(
            () => {
                dispatch(addPageAction(unitIdToLegacyUnitId(ownProps.resolveUnitId(ownProps.id)) as SectionName));
            },
            { priority: TaskPriority.UserBlocking }
        );
    },
    onCopyMultipleUnitInstance: async (bindingPath: AbsoluteResolvedModelPath, instance: string, unitId: UnitId) => {
        await dispatch(copyMultipleUnitInstanceAction(bindingPath, instance, unitId));
    },
    onClearMultipleUnit: (instanceId: string, method: ClearPageMethod) => {
        schedulerWrapper(
            () => {
                if (ownProps.unitPath != undefined) {
                    dispatch(
                        clearMultipleUnitInstanceAction(
                            ownProps.id?.pageId as SectionName,
                            ownProps.resolvePath(ownProps.unitPath),
                            instanceId,
                            method
                        )
                    );
                }
            },
            { priority: TaskPriority.UserBlocking }
        );
    },
    onClearPageSingleUnit: (method: ClearPageMethod) => {
        schedulerWrapper(
            () => {
                if (ownProps.unitPath != undefined) {
                    dispatch(
                        clearPageAction(
                            unitIdToLegacyUnitId(ownProps.resolveUnitId(ownProps.id)) as SectionName,
                            ownProps.resolvePath(ownProps.unitPath),
                            method
                        )
                    );
                }
            },
            { priority: TaskPriority.UserBlocking }
        );
    },
    onRemoveMultipleUnitInstance: (bindingPath: AbsoluteResolvedModelPath, instance: string, unitId: UnitId) => {
        schedulerWrapper(
            async () => {
                if (ownProps.hasAttachment) {
                    await dispatch(
                        removeAttachmentAction(bindingPath, instance, {
                            operationName: getRemoveLabelForChangeSet(ownProps.name),
                        })
                    );

                    return;
                }

                dispatch(removeMultipleUnitInstanceAction(bindingPath, instance, unitId, true));
                await dispatch(async (_, __, form) => {
                    await form.changeSet.send({ operationName: getRemoveLabelForChangeSet(ownProps.name) });
                });
            },
            { priority: TaskPriority.UserBlocking }
        );
    },
    onRemoveSingleUnit: (path: AbsoluteResolvedModelPath) => {
        schedulerWrapper(
            async () => {
                dispatch(
                    removePageAction(
                        unitIdToLegacyUnitId(ownProps.resolveUnitId(ownProps.id)) as SectionName,
                        ownProps.resolvePath(path)
                    )
                );
                await dispatch(async (_, __, form) => {
                    await form.changeSet.send({ operationName: getRemoveLabelForChangeSet(ownProps.name) });
                });
            },
            { priority: TaskPriority.UserBlocking }
        );
    },
    onScrollToUnit: async (unitId: UnitId): Promise<void> => {
        schedulerWrapper(() => dispatch(unitHighlightAction(unitId)), { priority: TaskPriority.UserVisible });
        schedulerWrapper(() => dispatch(scrollToUnitAction(unitId)), { priority: TaskPriority.Background });
    },
    isOptionalAndEmpty: () =>
        dispatch((_, __, form) =>
            isOptionalAndEmpty(unitIdToLegacyUnitId(ownProps.id) as SectionName, ownProps.optional ?? false, form)
        ),
});

export const UnitItemContainer = withResolvePaths(
    withResolveUnitId(
        withViewModelSelect(
            withEvaluators(
                connect(mapStateToProps, mapDispatchToProps, undefined, {
                    areStatePropsEqual: (next, prev) =>
                        Boolean(
                            isUnitIdEquals(next.id, prev.id) &&
                                areEqual(next.childUnitIds, prev.childUnitIds) &&
                                ((next.unitPath == undefined && prev.unitPath == undefined) ||
                                    (prev.unitPath != undefined &&
                                        next.unitPath != undefined &&
                                        next.unitPath.isEquals(prev.unitPath))) &&
                                next.hidden == prev.hidden &&
                                next.emptyUnitsVisible == prev.emptyUnitsVisible &&
                                next.filled == prev.filled &&
                                next.visible == prev.visible &&
                                next.hasVisibleChildren == prev.hasVisibleChildren &&
                                next.isChildItemActive == prev.isChildItemActive &&
                                next.isCurrent == prev.isCurrent
                        ),
                })(withVisibilityProps(UnitItem))
            )
        )
    )
);

function getEmptyUnitsVisible(state: FullFormState): boolean {
    const draftMeta = currentViewModelSelect.draftMeta(state);
    return draftMeta != undefined && draftMeta.emptyUnitsVisible != undefined ? draftMeta.emptyUnitsVisible : true;
}

function isUnitFilled(ownProps: UnitItemOwnProps, selfFilled: boolean, emptyUnits: UnitIdImmutableSet): boolean {
    const hasChildIds = getChildUnitIds(ownProps).some(x => UnitIdSet.hasItem(emptyUnits, x));
    return selfFilled || hasChildIds;
}
